import { useMemo } from 'react';
import type { InvestmentFlowPoint, InvestmentPortfolioEvolutionPoint } from '@/types';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useThemeVersion } from '@/lib/theme/useThemeColors';
import { SurfaceCard } from '@/components/ui/surface';
import {
  buildPortfolioEvolutionChartData,
  buildPortfolioEvolutionChartOption,
} from './InvestmentPortfolioEvolutionChart.helpers';

interface InvestmentPortfolioEvolutionChartProps {
  evolution?: InvestmentPortfolioEvolutionPoint[];
  trends?: InvestmentFlowPoint[];
  currentBalance: number;
  currentOriginal: number;
  currentProfit: number;
  className?: string;
}

export function InvestmentPortfolioEvolutionChart({
  evolution,
  trends = [],
  currentBalance,
  currentOriginal,
  currentProfit,
  className = '',
}: InvestmentPortfolioEvolutionChartProps) {
  const formatCurrency = useFormatCurrency();
  const themeVersion = useThemeVersion();
  const chartData = useMemo(
    () => buildPortfolioEvolutionChartData(evolution, trends, currentBalance, currentOriginal, currentProfit),
    [evolution, trends, currentBalance, currentOriginal, currentProfit],
  );
  const hasEstimated = chartData.some((d) => d.source === 'Estimated');

  // themeVersion: the builder snapshots themePalette() (line colors, markAreas,
  // symbol borders) — rebuild on a theme flip or those colors go stale.
  // formatCurrency: privacy-aware tooltip amounts re-mask when hide-values toggles.
  const option = useMemo<EChartsOption>(
    () => buildPortfolioEvolutionChartOption(chartData, formatCurrency),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartData, formatCurrency, themeVersion],
  );

  if (chartData.length === 0) {
    return (
      <SurfaceCard className={`flex h-full flex-col p-4 sm:p-6 ${className}`} data-testid="investment-evolution-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-text-primary sm:text-base">Evolução do Patrimônio</h3>
            <p className="mt-0.5 text-2xs text-text-secondary sm:text-xs">Sem histórico suficiente para montar a série</p>
          </div>
          <span className="rounded-full bg-surface-sunken px-2 py-1 text-2xs font-semibold text-text-secondary">
            Histórico
          </span>
        </div>
        <div className="flex min-h-40 flex-1 items-center justify-center text-sm text-text-secondary sm:min-h-56">
          Sem dados disponíveis
        </div>
      </SurfaceCard>
    );
  }

  return (
    <SurfaceCard className={`flex h-full flex-col p-4 sm:p-6 ${className}`} data-testid="investment-evolution-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-text-primary sm:text-base">Evolução do Patrimônio</h3>
          <p className="mt-0.5 text-2xs text-text-secondary sm:text-xs">
            Saldo histórico combinado com movimentações
          </p>
        </div>
        <div className="shrink-0 text-right">
          <span className="rounded-full bg-surface-sunken px-2 py-1 text-2xs font-semibold text-text-secondary">
            {hasEstimated ? 'Observado + estimado' : 'Observado'}
          </span>
        </div>
      </div>
      <div className="h-52 min-h-52 flex-1 sm:h-72 sm:min-h-72">
        <EChart option={option} />
      </div>
    </SurfaceCard>
  );
}
